import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Link } from '../components/Link.js';
import { controlFocus, minTarget } from '../primitives/shared.js';

export interface NavItem {
  href: string;
  label: string;
  /** Shown as text beside the label, never as a bare coloured dot. */
  badge?: string;
  disabled?: boolean;
}

export interface AppHeaderProps {
  productName: string;
  homeHref?: string;
  actions?: ReactNode;
  className?: string;
  /** SPA apps may replace the default `<a>` brand link (e.g. React Router `Link`). */
  renderBrand?: (props: { productName: string; homeHref: string }) => ReactNode;
}

/** Navigation chrome — masthead. Owned by the design system, not by applications. */
export function AppHeader({
  productName,
  homeHref = '/',
  actions,
  className,
  renderBrand,
}: AppHeaderProps) {
  return (
    <header
      className={cn(
        'sticky top-0 z-[var(--z-sticky)] flex items-center justify-between gap-field border-b border-line bg-raised px-gutter py-inline shadow-sticky',
        className,
      )}
    >
      {renderBrand ? (
        renderBrand({ productName, homeHref })
      ) : (
        <a
          href={homeHref}
          className={cn('text-h3 font-bold text-default no-underline', minTarget, controlFocus)}
        >
          {productName}
        </a>
      )}
      {actions ? <div className="flex items-center gap-inline">{actions}</div> : null}
    </header>
  );
}

export interface PrimaryNavProps {
  label: string;
  items: NavItem[];
  currentPath: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Primary navigation. The current item is marked with `aria-current="page"` and
 * a weight change, so position is not conveyed by colour alone.
 */
export function PrimaryNav({
  label,
  items,
  currentPath,
  orientation = 'vertical',
  className,
}: PrimaryNavProps) {
  return (
    <nav
      aria-label={label}
      className={cn(
        orientation === 'vertical'
          ? 'flex shrink-0 flex-col gap-xs border-b border-line bg-raised p-inline md:w-56 md:border-r md:border-b-0'
          : 'flex flex-wrap items-center gap-inline border-b border-line bg-raised px-gutter',
        className,
      )}
    >
      <ul
        className={cn(
          'm-0 flex list-none p-0',
          orientation === 'vertical' ? 'flex-col gap-xs' : 'flex-row gap-inline',
        )}
      >
        {items.map((item) => {
          const current = item.href === currentPath;
          return (
            <li key={item.href}>
              <Link
                href={item.href}
                current={current}
                aria-disabled={item.disabled || undefined}
                className={cn(
                  'flex items-center justify-between gap-inline rounded-control px-field py-inline text-body no-underline',
                  minTarget,
                  current ? 'bg-selected font-medium text-default' : 'text-muted',
                  !item.disabled && 'hov:bg-sunken',
                )}
              >
                {item.label}
                {item.badge ? <span className="text-body-sm text-muted">{item.badge}</span> : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export interface BreadcrumbsProps {
  items: { href?: string; label: string }[];
  className?: string;
  /** SPA apps may replace default PRM-02 links (e.g. React Router `Link`). */
  renderLink?: (item: { href: string; label: string }) => ReactNode;
}

/** Secondary navigation chrome. The final item is the current page and is not a link. */
export function Breadcrumbs({ items, className, renderLink }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-inline p-0 text-body-sm">
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-inline">
              {last || !item.href ? (
                <span aria-current={last ? 'page' : undefined} className="text-muted">
                  {item.label}
                </span>
              ) : renderLink ? (
                renderLink({ href: item.href, label: item.label })
              ) : (
                <Link href={item.href}>{item.label}</Link>
              )}
              {last ? null : (
                <span aria-hidden="true" className="text-muted">
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
